import { StoredAnalysis, AnalysisResult, LogEntry } from '../types/analysis';
import { getLogs, getAnalysis as apiGetAnalysis } from './api';
import { normalizeAnalysisData } from './dataUtils';

export async function getStoredAnalyses(page: number = 1, pageSize: number = 10): Promise<{
  items: StoredAnalysis[];
  total: number;
}> {
  try {
    const response = await getLogs(page, pageSize);
    console.log('API Log Response:', response);
    
    // Fetch full analysis data for each log entry
    const items = await Promise.all(
      response.items.map(async (log) => {
        try {
          console.log(`Processing log entry for ${log.requestId}:`, log);
          
          // Create a basic result with the log data
          const basicResult: StoredAnalysis = {
            requestId: log.requestId,
            status: log.status || 'completed',
            message: 'Loaded from log',
            storedAt: log.timestamp,
            source: 'api' as const,
            url: log.url,
            analysisTime: log.duration || log.metrics?.duration || 0,
            duration: log.duration || log.metrics?.duration || 0,
            mainPage: {
              url: log.url,
              text: '',
              images: [],
              chatWidgets: [],
              result: {}
            },
            summary: {
              businessName: 'Unknown Business',
              overall: 'Unknown',
              siteType: 'Unknown',
              jobsToDo: 'Unknown',
              goal: 'Unknown',
              role: 'Unknown',
              features: [],
              personaVoice: {
                tone: 'Unknown',
                style: 'Unknown',
                brandPersonality: 'Unknown',
                keyPhrases: [],
                emotionalTriggers: [],
                personaInstruction: 'Unknown',
                roleInstruction: 'Unknown'
              },
              objections: []
            },
            businessDetails: {
              companyName: 'Unknown Business',
              website: '',
              phone: '',
              email: '',
              socialHandles: {},
              contact: ''
            }
          };
          
          // Try to fetch the full analysis to get the business name and other details
          try {
            const analysis = await getAnalysis(log.requestId);
            
            // If we have the full analysis, extract the business name and other details
            if (analysis) {
              console.log(`Using business name from API response for ${log.requestId}:`, analysis.summary?.businessName);
              return {
                ...analysis,
                requestId: log.requestId,
                storedAt: log.timestamp,
                source: 'api' as const,
                url: log.url || analysis.mainPage?.url || '',
                status: log.status || 'completed',
                analysisTime: analysis.analysisTime || log.metrics?.duration || 0
              };
            }
            
            return basicResult;
          } catch (apiError) {
            console.log(`API fetch failed for ${log.requestId}, using basic result:`, apiError);
            return basicResult;
          }
        } catch (error) {
          console.error(`Error processing log for ${log.requestId}:`, error);
          // Return a minimal version if processing fails
          return {
            requestId: log.requestId,
            message: 'Analysis data unavailable',
            mainPage: { 
              url: log.url, 
              text: '', 
              images: [], 
              chatWidgets: [], 
              result: {} 
            },
            storedAt: log.timestamp,
            source: 'api' as const,
            url: log.url,
            status: log.status || 'error',
            analysisTime: log.metrics?.duration || 0,
            summary: {
              businessName: 'Unknown Business',
              overall: 'Unknown',
              siteType: 'Unknown',
              jobsToDo: 'Unknown',
              goal: 'Unknown',
              role: 'Unknown',
              features: [],
              personaVoice: {
                tone: 'Unknown',
                style: 'Unknown',
                brandPersonality: 'Unknown',
                keyPhrases: [],
                emotionalTriggers: [],
                personaInstruction: 'Unknown',
                roleInstruction: 'Unknown'
              },
              objections: []
            }
          };
        }
      })
    );
    
    return {
      items,
      total: response.total
    };
  } catch (error) {
    console.error('Error fetching stored analyses:', error);
    return { items: [], total: 0 };
  }
}

export async function getAnalysis(requestId: string): Promise<AnalysisResult> {
  try {
    // Get the raw analysis data from the API
    const data = await apiGetAnalysis(requestId);
    console.log('Raw API response for getAnalysis:', data);
    
    // Use the helper function to normalize the data structure
    return normalizeAnalysisData(data, requestId);
  } catch (error) {
    console.error('Error enhancing analysis data:', error);
    throw error;
  }
}

export async function getStoredAnalysis(requestId: string): Promise<StoredAnalysis | null> {
  try {
    // Directly get the analysis from the API
    const analysis = await getAnalysis(requestId);
    console.log(`Normalized analysis data for ${requestId}:`, analysis);
    
    // The analysis now has the correct structure,
    // we just need to ensure the required fields for StoredAnalysis
    const result: StoredAnalysis = {
      ...analysis,
      requestId: analysis.requestId || requestId,
      storedAt: analysis.timestamp || new Date().toISOString(),
      source: 'api' as const,
      url: analysis.mainPage?.url || ((analysis as any).url) || '',
      status: (analysis as any).status || 'completed',
      analysisTime: analysis.analysisTime || 0
    };
    
    console.log(`Processed analysis result for ${requestId}:`, {
      url: result.url,
      businessName: result.summary?.businessName,
      analysisTime: result.analysisTime
    });
    
    return result;
  } catch (error) {
    console.error(`Error fetching stored analysis for ${requestId}:`, error);
    return null;
  }
}

// Remove local storage functions as they're no longer needed
export const clearStoredAnalyses = async (): Promise<void> => {
  // This is now a no-op as we don't want to clear server-side storage
  console.log('Clearing stored analyses is no longer supported');
};

export const removeStoredAnalysis = async (requestId: string): Promise<void> => {
  // This is now a no-op as we don't want to remove server-side storage
  console.log('Removing stored analysis is no longer supported');
}; 